import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { map } from 'rxjs/operators';
import { AuthenticationStateHelperService } from '../_helpers/authentication-state-helper/authentication-state-helper.service';
import { CanUseService } from '../_helpers/can-use/can-use.service';

/** What a route needs, in its data as `canUse`. See CanUseService.canUse. */
export interface CanUseRouteData {
  feature?: string;
  switchKey?: string;
  /** A role the user must hold as well, e.g. ADMIN for System Controls. */
  role?: string;
}

/**
 * Lets a signed-in user onto the route only when CanUseService.canUse passes for the route's `data.canUse`.
 * Otherwise it sends them to sign in and flags the token as unauthorised, as the pages' own checks did.
 * Use it after AuthGuard: a signed-out visitor is AuthGuard's to redirect, so this guard lets them past.
 *
 *   { path: 'email-history', canActivate: [AuthGuard, canUseGuard], data: { canUse: { feature: 'EmailHistoryView' } } }
 */
export const canUseGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  if (!localStorage.getItem('currentUser')) {
    return true;
  }

  const router = inject(Router);
  const authenticationState = inject(AuthenticationStateHelperService);
  const wanted: CanUseRouteData = route.data?.['canUse'] ?? {};

  return inject(CanUseService).canUse(wanted.feature, wanted.switchKey, wanted.role).pipe(
    map(allowed => {
      if (allowed) {
        return true;
      }
      console.error(`User does not have the required permissions to open ${state.url}`);
      authenticationState.unauthorizedToken = true;
      return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
    })
  );
};
